import {
	Checkbox,
	Divider,
	InputAdornment,
	Stack,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import React, {useState} from 'react';
import {Controller, useFormContext} from 'react-hook-form';

import {AlertInfo} from 'presentation/components';
import {ControlledRadioGroup} from 'presentation/components/ControlledRadioGroup';
import {FeaturesEnum, QueryParamsForTaggingEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {AccordionWithStatus} from 'presentation/pages/private/refund/components/AccordionWithStatus';
import {StatusEnum} from 'presentation/pages/private/refund/constants/accordionByStatus';
import {translations} from 'presentation/translations-new';
import {formatCurrency} from 'presentation/utils';
import {useAccordionExpandedStore} from '../../../../store/useAccordionExpandedStore';
import {AccordionExpandedEnum} from '../../../../types';
import {CityAutoComplete} from '../CityAutoComplete';
import {StateAutoComplete} from '../StateAutoComplete';
import {Modal} from './components/Modal';
import {ISSUE_INVOICE_OPTS} from './constants/formOpts';
import {AlertInfoContainer, StyledCheckbox} from './styles';

const {
	title,
	alertInfo,
	canEmit,
	numFiscalDoc,
	verifyCode,
	verifyCodeLegacy,
	valueFiscalDoc,
	state: stateText,
	city,
	digitNumPlaceholder,
	digitCodePlaceholder,
	valueFiscalDocPlaceholder,
	nfAlert,
	nfAlertInfo,
} = translations['pt-br'].privateFlow.refund.receiptRegistrationPage.fiscalDocument;

const {requiredField} = translations['pt-br'].commons;

export const FiscalDocument = () => {
	const {isTablet} = useMediaQueries();
	const {setSearchParams} = useQueryParams();
	const [refundInfoAlert, setRefundInfoAlert] = useState(false);
	const {isEnabled: isAccessKeyEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_REFUND_REQUEST_ACCESS_KEY,
	);

	const verifyCodeLabel = isAccessKeyEnabled ? verifyCode : verifyCodeLegacy;
	const accordionExpanded = useAccordionExpandedStore(state => state.accordion);
	const setAccordionExpanded = useAccordionExpandedStore(state => state.setAccordion);

	const theme = useTheme();

	const {
		control,
		watch,
		formState: {errors},
		clearErrors,
		resetField,
	} = useFormContext();

	const handleToggleModal = () => {
		setRefundInfoAlert(prev => !prev);
	};

	const canEmitFiscalDocumentWatch = watch('canEmitFiscalDocument') === 'yes';

	const showTextErrorFiscalDocumentNumber = errors.numFiscalDocument
		? `${nfAlert}\n${requiredField}`
		: nfAlert;
	const showTextErrorFiscalDocumentPrice = errors.priceFiscalDocument?.message
		? String(errors.priceFiscalDocument.message)
		: null;
	const showTextErrorVerifyCode = errors.verifyCode ? requiredField : null;
	const showTextErrorState = errors.state ? requiredField : null;
	const checkboxColor = errors.termsChecked
		? theme.palette.error.main
		: theme.palette.text.primary;

	const isAccordionExpanded =
		accordionExpanded > AccordionExpandedEnum.FISCAL_DOCUMENT
			? StatusEnum.FINISHED
			: StatusEnum.PENDING;

	const isFinished = isAccordionExpanded === StatusEnum.FINISHED;

	const isExpanded = accordionExpanded === AccordionExpandedEnum.FISCAL_DOCUMENT;

	const isAccordionCloseAndFinished = !isExpanded && isFinished;

	const handleEdit = () => {
		if (isAccordionCloseAndFinished) {
			setAccordionExpanded(AccordionExpandedEnum.FISCAL_DOCUMENT);
		}
	};

	return (
		<AccordionWithStatus
			expanded={isExpanded}
			onClick={handleEdit}
			title={title}
			removeIcon
			cursor={isAccordionCloseAndFinished ? 'pointer' : 'default'}
			status={isAccordionExpanded}>
			<Stack gap={2}>
				<AlertInfoContainer severity="info">
					<AlertInfo.Title sx={{fontWeight: 400}} title={alertInfo.title} />
					<AlertInfo.Description>
						<AlertInfo.Action
							onClick={handleToggleModal}
							$iconName="arrow-right-filled">
							{alertInfo.action}
						</AlertInfo.Action>
					</AlertInfo.Description>
				</AlertInfoContainer>
				<Stack gap={1}>
					<Typography color="primary" fontWeight={600}>
						{canEmit}
					</Typography>

					<Controller
						control={control}
						name="canEmitFiscalDocument"
						render={({field: {value, onChange}}) => (
							<ControlledRadioGroup
								gap="16px"
								direction="row"
								radioOpts={ISSUE_INVOICE_OPTS}
								key={value}
								value={value}
								onChange={event => {
									clearErrors();
									resetField('numFiscalDocument');
									resetField('priceFiscalDocument');
									resetField('verifyCode');
									resetField('state');
									resetField('city');
									resetField('termsChecked');

									if (event.target.value === 'no') {
										setSearchParams(prev => {
											prev.delete(
												QueryParamsForTaggingEnum.REQUEST_REFUND_UF,
											);
											prev.delete(
												QueryParamsForTaggingEnum.REQUEST_REFUND_CITY,
											);
											return prev;
										});
									}

									onChange(event);
								}}
							/>
						)}
					/>
				</Stack>

				{canEmitFiscalDocumentWatch ? (
					<Stack gap={2}>
						<Stack gap={1}>
							<Typography color="primary" fontWeight={600}>
								{numFiscalDoc}
							</Typography>
							<Controller
								control={control}
								name="numFiscalDocument"
								render={({field: {value, onChange}}) => (
									<TextField
										error={!!errors.numFiscalDocument}
										helperText={errors.numFiscalDocument ? requiredField : null}
										value={value}
										onChange={event => {
											const filteredValue = event.target.value.replace(
												/\D/g,
												'',
											);
											onChange(filteredValue);
										}}
										slotProps={{htmlInput: {inputMode: 'numeric'}}}
										placeholder={digitNumPlaceholder}
										label={digitNumPlaceholder}
									/>
								)}
							/>
						</Stack>

						<Stack flexDirection={isTablet ? 'column' : 'row'} gap={2}>
							<Stack gap={1} flex={1}>
								<Typography color="primary" fontWeight={600}>
									{valueFiscalDoc}
								</Typography>
								<Controller
									control={control}
									name="priceFiscalDocument"
									render={({field: {value, onChange}}) => (
										<TextField
											error={!!errors.priceFiscalDocument}
											helperText={showTextErrorFiscalDocumentPrice}
											value={value}
											onChange={event => {
												const formatted = formatCurrency(
													event.target.value,
												);

												onChange(formatted);
											}}
											slotProps={{
												htmlInput: {inputMode: 'numeric'},
												input: {
													startAdornment: value ? (
														<InputAdornment position="start">
															R${' '}
														</InputAdornment>
													) : null,
												},
											}}
											placeholder={valueFiscalDocPlaceholder}
											label={valueFiscalDocPlaceholder}
										/>
									)}
								/>
							</Stack>
							<Stack gap={1} flex={1}>
								<Typography color="primary" fontWeight={600}>
								{verifyCodeLabel}
								</Typography>
								<Controller
									control={control}
									name="verifyCode"
									render={({field: {value, onChange}}) => (
										<TextField
											error={!!errors.verifyCode}
											helperText={showTextErrorVerifyCode}
											value={value}
											onChange={onChange}
											placeholder={digitCodePlaceholder}
											label={digitCodePlaceholder}
										/>
									)}
								/>
							</Stack>
						</Stack>
						<Stack direction={!isTablet ? 'row' : 'column'} gap={2}>
							<Stack gap={1} flex={1}>
								<Typography color="primary" fontWeight={600}>
									{stateText}
								</Typography>
								<StateAutoComplete
									name="state"
									clearDependentFieldWhenChange="city"
									error={!!errors.state}
									helperText={showTextErrorState}
								/>
							</Stack>
							<Stack gap={1} flex={1}>
								<Typography color="primary" fontWeight={600}>
									{city}
								</Typography>
								<CityAutoComplete name="city" />
							</Stack>
						</Stack>
					</Stack>
				) : (
					<Stack gap={2}>
						<Stack flexDirection={isTablet ? 'column' : 'row'} gap={2}>
							<Stack gap={1} flex={1}>
								<Typography color="primary" fontWeight={600}>
									{numFiscalDoc}
								</Typography>
								<Controller
									control={control}
									name="numFiscalDocument"
									render={({field: {value, onChange}}) => (
										<TextField
											error={!!errors.numFiscalDocument}
											helperText={showTextErrorFiscalDocumentNumber}
											value={value}
											onChange={event => {
												const filteredValue = event.target.value.replace(
													/\D/g,
													'',
												);
												onChange(filteredValue);
											}}
											slotProps={{
												htmlInput: {inputMode: 'numeric'},
												formHelperText: {
													sx: {
														whiteSpace: 'pre-line',
													},
												},
											}}
											placeholder={digitNumPlaceholder}
											label={digitNumPlaceholder}
										/>
									)}
								/>
							</Stack>
							<Stack gap={1} flex={1}>
								<Typography color="primary" fontWeight={600}>
									{valueFiscalDoc}
								</Typography>
								<Controller
									control={control}
									name="priceFiscalDocument"
									render={({field: {value, onChange}}) => (
										<TextField
											error={!!errors.priceFiscalDocument}
											helperText={showTextErrorFiscalDocumentPrice}
											value={value}
											onChange={event => {
												const formatted = formatCurrency(
													event.target.value,
												);
												onChange(formatted);
											}}
											slotProps={{
												htmlInput: {inputMode: 'numeric'},
												input: {
													startAdornment: value ? (
														<InputAdornment position="start">
															R${' '}
														</InputAdornment>
													) : null,
												},
											}}
											placeholder={valueFiscalDocPlaceholder}
											label={valueFiscalDocPlaceholder}
										/>
									)}
								/>
							</Stack>
						</Stack>

						<Divider />

						<Controller
							control={control}
							name="termsChecked"
							render={({field: {value, onChange}}) => (
								<StyledCheckbox
									control={
										<Checkbox
											checked={value}
											onChange={(_, checked) => {
												onChange(checked);
											}}
											sx={{
												color: checkboxColor,
											}}
										/>
									}
									label={
										<Stack>
											<Typography
												variant="body1"
												color={checkboxColor}
												lineHeight={1.5}>
												{nfAlertInfo}
											</Typography>
											{errors.termsChecked ? (
												<Typography
													variant="helperText"
													color={theme.palette.error.main}>
													{requiredField}
												</Typography>
											) : null}
										</Stack>
									}
								/>
							)}
						/>
					</Stack>
				)}

				<Modal open={refundInfoAlert} setOpen={handleToggleModal} />
			</Stack>
		</AccordionWithStatus>
	);
};
